/**
 * <midnight-countdown> — one shared clock for the announcement bar and the offer block.
 *
 * Two modes, chosen by the theme setting "sale_end" (Liquid renders it as data-end):
 * - sale_end empty (no data-end): counts down to the next local midnight and starts again
 *   every day. Every instance computes the same deadline from Date.now(), so all counters on
 *   the page show the same second.
 * - sale_end set (data-end = Unix seconds): counts down to that moment. Once it has passed,
 *   the closest [data-sale-urgency] wrapper (or the element itself) is hidden, so no counter
 *   ever sits at 00:00:00.
 *
 * Usage: wrap markup containing elements with data-countdown="days|hours|minutes|seconds".
 * With a "days" element present, hours run 0-23 and the days unit ([data-countdown-unit="days"])
 * is hidden once less than 24 hours remain. Without one (announcement bar), hours run on.
 */
if (!customElements.get('midnight-countdown')) {
  customElements.define(
    'midnight-countdown',
    class MidnightCountdown extends HTMLElement {
      connectedCallback() {
        this._midnight = !this.hasAttribute('data-end');
        this._end = parseInt(this.getAttribute('data-end'), 10) * 1000;
        this._tick = this._tick.bind(this);
        if (this._tick()) this._interval = setInterval(this._tick, 1000);
      }

      disconnectedCallback() {
        clearInterval(this._interval);
      }

      _expire() {
        clearInterval(this._interval);
        (this.closest('[data-sale-urgency]') || this).hidden = true;
        return false;
      }

      _nextMidnight() {
        const d = new Date();
        d.setHours(24, 0, 0, 0);
        return d.getTime();
      }

      _tick() {
        if (this._midnight) this._end = this._nextMidnight();
        if (!Number.isFinite(this._end)) return this._expire();
        const diff = this._end - Date.now();
        if (diff <= 0) return this._expire();

        const total = Math.floor(diff / 1000);
        const pad = (n) => String(n).padStart(2, '0');
        const daysEls = this.querySelectorAll('[data-countdown="days"]');
        const days = Math.floor(total / 86400);
        const h = pad(daysEls.length ? Math.floor((total % 86400) / 3600) : Math.floor(total / 3600));
        const m = pad(Math.floor((total % 3600) / 60));
        const s = pad(total % 60);

        daysEls.forEach((el) => (el.textContent = String(days)));
        this.querySelectorAll('[data-countdown-unit="days"]').forEach((el) => (el.hidden = days < 1));
        this.querySelectorAll('[data-countdown="hours"]').forEach((el) => (el.textContent = h));
        this.querySelectorAll('[data-countdown="minutes"]').forEach((el) => (el.textContent = m));
        this.querySelectorAll('[data-countdown="seconds"]').forEach((el) => (el.textContent = s));
        return true;
      }
    }
  );
}
